import { cn } from "@/lib/utils";
import { Popover, PopoverTrigger, PopoverContent } from "./popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
} from "./command";
import { ChevronsUpDown, Check, LucideIcon } from "lucide-react";
import React from "react";
import { Button } from "./button";
import { useCappedList } from "@/hooks/useCappedList";

/**
 * Parses a prefixed value created by MultiSelectItem's valuePrefix prop.
 * Returns the prefix and the original value.
 */
function parseMultiSelectValue(prefixedValue: string): {
  prefix: string | null;
  value: string;
} {
  const dashIndex = prefixedValue.indexOf("-");
  if (dashIndex === -1) {
    return { prefix: null, value: prefixedValue };
  }
  return {
    prefix: prefixedValue.substring(0, dashIndex),
    value: prefixedValue.substring(dashIndex + 1),
  };
}

/**
 * Groups an array of prefixed values by their prefix.
 * Useful for processing MultiSelect form submissions.
 */
function groupMultiSelectValues(
  prefixedValues: string[]
): Record<string, string[]> {
  const grouped: Record<string, string[]> = {};

  for (const prefixedValue of prefixedValues) {
    const { prefix, value } = parseMultiSelectValue(prefixedValue);
    const key = prefix ?? "_unprefixed";
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(value);
  }

  return grouped;
}

// Context to share state between compound components
type MultiSelectContextValue = {
  onChange: (value: string[]) => void;
  getLabel: (value: string) => string;
  value: string[];
  open: boolean;
};

const MultiSelectContext = React.createContext<MultiSelectContextValue | null>(
  null
);

function useMultiSelectContext() {
  const context = React.useContext(MultiSelectContext);
  if (!context) {
    throw new Error(
      "MultiSelect compound components must be used within <MultiSelect>"
    );
  }
  return context;
}

type MultiSelectProps = {
  onChange: (value: string[]) => void;
  getLabel: (value: string) => string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  value: string[];
};

function MultiSelect({
  defaultOpen = false,
  children,
  onChange,
  getLabel,
  value,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <MultiSelectContext.Provider value={{ value, onChange, open, getLabel }}>
      <Popover modal={true} open={open} onOpenChange={setOpen}>
        {children}
      </Popover>
    </MultiSelectContext.Provider>
  );
}

function MultiSelectTrigger({
  id,
  placeholder,
  icon: Icon,
}: {
  id?: string;
  placeholder?: string;
  icon?: LucideIcon;
}) {
  const { open, getLabel, value } = useMultiSelectContext();

  const formatCappedList = useCappedList();

  return (
    <PopoverTrigger asChild>
      <Button
        id={id}
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={cn("w-[200px] justify-between text-left font-normal")}
      >
        {Icon && <Icon className="size-4 text-muted-foreground" />}
        <span
          className={cn(
            "flex-1 truncate",
            !value.length && "text-muted-foreground hover:text-muted-foreground"
          )}
        >
          {value.length
            ? formatCappedList(value.map(getLabel), { cap: 3 })
            : placeholder}
        </span>
        <ChevronsUpDown className="opacity-50" />
      </Button>
    </PopoverTrigger>
  );
}

function MultiSelectContent({ children }: { children: React.ReactNode }) {
  return (
    <PopoverContent className="p-0 w-(--radix-popover-trigger-width)">
      <Command>{children}</Command>
    </PopoverContent>
  );
}

function MultiSelectSearch({ placeholder }: { placeholder: string }) {
  return <CommandInput placeholder={placeholder} className="h-9" />;
}

function MultiSelectGroup({
  heading,
  children,
}: {
  heading?: string;
  children: React.ReactNode;
}) {
  return <CommandGroup heading={heading}>{children}</CommandGroup>;
}

function MultiSelectList({ children }: { children: React.ReactNode }) {
  return <CommandList className="max-h-64">{children}</CommandList>;
}

function MultiSelectItem({
  value: _value,
  valuePrefix,
  label,
}: {
  valuePrefix?: string;
  value: string;
  label: string;
}) {
  const { value: selectedValues, onChange } = useMultiSelectContext();

  const value = valuePrefix ? `${valuePrefix}-${_value}` : _value;

  const isSelected = selectedValues.includes(value);

  return (
    <CommandItem
      value={value}
      keywords={[value, label]}
      onSelect={() => {
        onChange(
          isSelected
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value]
        );
      }}
    >
      {label}
      <Check
        className={cn("ml-auto", isSelected ? "opacity-100" : "opacity-0")}
      />
    </CommandItem>
  );
}

export {
  MultiSelect,
  MultiSelectTrigger,
  MultiSelectContent,
  MultiSelectSearch,
  MultiSelectGroup,
  MultiSelectList,
  MultiSelectItem,
  parseMultiSelectValue,
  groupMultiSelectValues,
};
